import { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useOutlet } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'

import { NativePlatform, WebPlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { breakpoints } from '@/config/layout'
import { ContentArea } from '@/layout/components/content-area/ContentArea'
import { useAppointments } from '@/modules/appointment/hooks/useAppointments'
import { useEncounters } from '@/modules/encounter/hooks/useEncounters'
import { AgendaSyncModal } from '@/modules/shared/components/agenda-sync-modal/AgendaSyncModal'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { Timeline } from '../../components/timeline/Timeline'
import { useIgnoreSyncAgenda } from '../../hooks/useIgnoreSyncAgenda'
import { type TimelineContextType, TimelineErrorState } from '../../types/timeline-context.type'
import { TimelineTodayPage } from '../today/TimelineToday.page'
import { TimelineGrid, TimelineNativeContainer } from './TimelineRoot.styled'

import type { Appointment } from '@/modules/appointment/types/appointment.types'

const TimelineRoot = () => {
  const outlet = useOutlet()
  const location = useLocation()
  const isMinMedium = useMediaQuery(breakpoints.fromM)

  const [showSyncModal, setShowSyncModal] = useState(false)
  const [heldChanges, setHeldChanges] = useState<Record<number, Partial<Appointment>>>({})
  const { hasIgnoredSyncAgenda, setIgnoreSyncAgenda } = useIgnoreSyncAgenda()

  const {
    getAppointments: {
      execute: getAppointments,
      value: getAppointmentsValue,
      isLoading: getAppointmentsIsLoading,
      status: getAppointmentsStatus,
    },
  } = useAppointments()

  const {
    getEncounters: {
      execute: getEncounters,
      value: getEncountersValue,
      isLoading: getEncountersIsLoading,
      status: getEncountersStatus,
    },
  } = useEncounters()

  // Template variables
  // ------------------------------------------------------------------------- /
  const timelineError =
    (getAppointmentsIsLoading && getAppointmentsStatus === 'error') ||
    (getEncountersIsLoading && getEncountersStatus === 'error')
      ? TimelineErrorState.GENERIC
      : undefined

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    getAppointments()
    getEncounters()
  }, [])

  useEffect(() => {
    const hasFutureAppointments = getAppointmentsValue?.some((appointment) => new Date(appointment.start) > new Date())

    if (!hasIgnoredSyncAgenda && hasFutureAppointments) {
      setShowSyncModal(true)
    }
  }, [getAppointmentsValue, hasIgnoredSyncAgenda])

  // A fetch that lands is the authority, so anything held over it is dropped.
  useEffect(() => {
    setHeldChanges({})
  }, [getAppointmentsValue])

  // Methods
  // ------------------------------------------------------------------------- /
  const patchAppointment = useCallback((id: number, patch: Partial<Appointment>) => {
    setHeldChanges((current) => ({ ...current, [id]: { ...current[id], ...patch } }))
  }, [])

  const appointmentsValue = useMemo(() => {
    if (!getAppointmentsValue || Object.keys(heldChanges).length === 0) return getAppointmentsValue

    return getAppointmentsValue.map((appointment) =>
      heldChanges[appointment.id] ? { ...appointment, ...heldChanges[appointment.id] } : appointment,
    )
  }, [getAppointmentsValue, heldChanges])

  const forceReload = () => {
    getAppointments()
    getEncounters()
  }

  function onHideModal() {
    setShowSyncModal(false)
    setIgnoreSyncAgenda(true)
  }

  const timelineProps: TimelineContextType = {
    appointments: {
      value: appointmentsValue,
      status: getAppointmentsStatus,
      isLoading: getAppointmentsIsLoading,
    },
    encounters: {
      value: getEncountersValue,
      status: getEncountersStatus,
      isLoading: getEncountersIsLoading,
    },
    isLoading:
      getAppointmentsIsLoading ||
      getEncountersIsLoading ||
      getAppointmentsStatus === 'idle' ||
      getEncountersStatus === 'idle',
    error: timelineError,
    forceReload,
    patchAppointment,
  }

  if (timelineProps.isLoading) {
    return <AnimatedLoader />
  }

  return (
    <>
      <TimelineGrid>
        <ContentArea gridArea='content' divider>
          <WebPlatform>
            <Timeline {...timelineProps} />
          </WebPlatform>
          <NativePlatform>
            <TimelineNativeContainer>
              <Timeline {...timelineProps} />
            </TimelineNativeContainer>
          </NativePlatform>
        </ContentArea>

        <WebPlatform>
          {location.pathname === '/timeline' && isMinMedium ? <TimelineTodayPage {...timelineProps} /> : null}
        </WebPlatform>

        {outlet ? (
          <ContentArea gridArea='content-detail'>
            <Outlet context={timelineProps} />
          </ContentArea>
        ) : null}
      </TimelineGrid>
      <AgendaSyncModal isVisible={showSyncModal} onClose={onHideModal} />
    </>
  )
}

export { TimelineRoot }
